import React, { useEffect, useRef, useState } from 'react';

export const Button = ({ onClick, children, disabled }) => (
  <button
    onClick={onClick}
    onTouchStart={() => {}}
    onTouchEnd={(e) => {
      if (onClick) {
        onClick();
      }
    }}
    disabled={disabled}
    className="p-2 rounded font-game text-gray-300 bg-purple-700 m-1 cursor-pointer"
    style={{ userSelect: 'none' }}
  >
    {children}
  </button>
);

export const Joystick = ({ onMove, position }) => {
  const joystickRef = useRef(null);
  const [stickPosition, setStickPosition] = useState({ dx: 0, dy: 0 });
  const activeTouches = useRef(new Map());
  const lastMove = useRef({ dx: 0, dy: 0 });
  const animationFrameId = useRef(null);
  const activeKeys = useRef(new Set());
  const STICK_SIZE = 128;
  const INNER_SIZE = 64;
  const MAX_DISTANCE = (STICK_SIZE - INNER_SIZE) / 2;

  const calculateStickPosition = (clientX, clientY, rect) => {
    const centerX = rect.left + STICK_SIZE / 2;
    const centerY = rect.top + STICK_SIZE / 2;

    let dx = clientX - centerX;
    let dy = clientY - centerY;

    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > MAX_DISTANCE) {
      dx = (dx / distance) * MAX_DISTANCE;
      dy = (dy / distance) * MAX_DISTANCE;
    }

    return {
      dx: dx / MAX_DISTANCE,
      dy: dy / MAX_DISTANCE,
      pixelX: dx,
      pixelY: dy,
    };
  };

  const startContinuousMove = () => {
    const update = () => {
      if (activeTouches.current.size > 0 || activeKeys.current.size > 0) {
        onMove(lastMove.current.dx, lastMove.current.dy);
        animationFrameId.current = requestAnimationFrame(update);
      }
    };
    if (!animationFrameId.current) {
      animationFrameId.current = requestAnimationFrame(update);
    }
  };

  const stopContinuousMove = () => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    onMove(0, 0);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      let dx = 0;
      let dy = 0;

      if (e.key.toLowerCase() === 'w' || e.key === 'ArrowUp') {
        activeKeys.current.add('up');
      }
      if (e.key.toLowerCase() === 's' || e.key === 'ArrowDown') {
        activeKeys.current.add('down');
      }
      if (e.key.toLowerCase() === 'a' || e.key === 'ArrowLeft') {
        activeKeys.current.add('left');
      }
      if (e.key.toLowerCase() === 'd' || e.key === 'ArrowRight') {
        activeKeys.current.add('right');
      }

      if (activeKeys.current.has('up')) dy -= 1;
      if (activeKeys.current.has('down')) dy += 1;
      if (activeKeys.current.has('left')) dx -= 1;
      if (activeKeys.current.has('right')) dx += 1;

      if (dx !== 0 || dy !== 0) {
        const length = Math.sqrt(dx * dx + dy * dy);
        dx /= length;
        dy /= length;
        lastMove.current = { dx, dy };
        setStickPosition({ dx: dx * MAX_DISTANCE, dy: dy * MAX_DISTANCE });
        startContinuousMove();
      }
    };

    const handleKeyUp = (e) => {
      if (e.key.toLowerCase() === 'w' || e.key === 'ArrowUp') activeKeys.current.delete('up');
      if (e.key.toLowerCase() === 's' || e.key === 'ArrowDown') activeKeys.current.delete('down');
      if (e.key.toLowerCase() === 'a' || e.key === 'ArrowLeft') activeKeys.current.delete('left');
      if (e.key.toLowerCase() === 'd' || e.key === 'ArrowRight') activeKeys.current.delete('right');

      if (activeKeys.current.size === 0) {
        lastMove.current = { dx: 0, dy: 0 };
        setStickPosition({ dx: 0, dy: 0 });
        stopContinuousMove();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    const joystickElement = joystickRef.current;

    const handleTouchStart = (e) => {
      e.preventDefault();

      const rect = joystickElement.getBoundingClientRect();

      Array.from(e.changedTouches).forEach((touch) => {
        if (
          touch.clientX >= rect.left &&
          touch.clientX <= rect.right &&
          touch.clientY >= rect.top &&
          touch.clientY <= rect.bottom
        ) {
          const { dx, dy, pixelX, pixelY } = calculateStickPosition(
            touch.clientX,
            touch.clientY,
            rect
          );

          activeTouches.current.set(touch.identifier, {
            touch,
            pixelX,
            pixelY,
            dx,
            dy,
          });

          lastMove.current = { dx, dy };
          setStickPosition({ dx: pixelX, dy: pixelY });
          startContinuousMove();
        }
      });
    };

    const handleTouchMove = (e) => {
      e.preventDefault();

      const rect = joystickElement.getBoundingClientRect();

      Array.from(e.changedTouches).forEach((touch) => {
        if (activeTouches.current.has(touch.identifier)) {
          const { dx, dy, pixelX, pixelY } = calculateStickPosition(
            touch.clientX,
            touch.clientY,
            rect
          );

          activeTouches.current.set(touch.identifier, {
            touch,
            pixelX,
            pixelY,
            dx,
            dy,
          });

          lastMove.current = { dx, dy };
          setStickPosition({ dx: pixelX, dy: pixelY });
        }
      });
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();

      Array.from(e.changedTouches).forEach((touch) => {
        if (activeTouches.current.has(touch.identifier)) {
          activeTouches.current.delete(touch.identifier);
          if (activeTouches.current.size === 0) {
            setStickPosition({ dx: 0, dy: 0 });
            lastMove.current = { dx: 0, dy: 0 };
            stopContinuousMove();
          }
        }
      });
    };

    joystickElement.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    joystickElement.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    joystickElement.addEventListener('touchend', handleTouchEnd, {
      passive: false,
    });
    joystickElement.addEventListener('touchcancel', handleTouchEnd, {
      passive: false,
    });

    return () => {
      joystickElement.removeEventListener('touchstart', handleTouchStart);
      joystickElement.removeEventListener('touchmove', handleTouchMove);
      joystickElement.removeEventListener('touchend', handleTouchEnd);
      joystickElement.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    const joystickElement = joystickRef.current;

    const handleMouseDown = (e) => {
      e.preventDefault();

      const rect = joystickElement.getBoundingClientRect();

      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        const { dx, dy, pixelX, pixelY } = calculateStickPosition(
          e.clientX,
          e.clientY,
          rect
        );

        activeTouches.current.set('mouse', { dx, dy, pixelX, pixelY });
        lastMove.current = { dx, dy };
        setStickPosition({ dx: pixelX, dy: pixelY });
        startContinuousMove();

        const handleMouseMove = (eMove) => {
          const { dx, dy, pixelX, pixelY } = calculateStickPosition(
            eMove.clientX,
            eMove.clientY,
            rect
          );

          activeTouches.current.set('mouse', { dx, dy, pixelX, pixelY });
          lastMove.current = { dx, dy };
          setStickPosition({ dx: pixelX, dy: pixelY });
        };

        const handleMouseUp = () => {
          activeTouches.current.delete('mouse');
          setStickPosition({ dx: 0, dy: 0 });
          lastMove.current = { dx: 0, dy: 0 };
          stopContinuousMove();
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }
    };

    joystickElement.addEventListener('mousedown', handleMouseDown);

    return () => {
      joystickElement.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return (
    <div
      ref={joystickRef}
      className="joystick-area absolute rounded-full bg-gray-800 bg-opacity-30 flex items-center justify-center"
      style={{
        userSelect: 'none',
        width: `${STICK_SIZE}px`,
        height: `${STICK_SIZE}px`,
        ...position,
      }}
    >
      <div
        className="joystick-inner absolute bg-gray-400 bg-opacity-70 rounded-full"
        style={{
          width: `${INNER_SIZE}px`,
          height: `${INNER_SIZE}px`,
          transform: `translate(${stickPosition.dx}px, ${stickPosition.dy}px)`,
          left: `${(STICK_SIZE - INNER_SIZE) / 2}px`,
          top: `${(STICK_SIZE - INNER_SIZE) / 2}px`,
        }}
      />
      <div className="absolute top-0 text-white text-lg font-bold">WASD</div>
    </div>
  );
};

export const Sprite = React.forwardRef(({
  emoji,
  src,
  size = 40,
  style = {},
  className = '',
  ...props
}, ref) => {
  if (emoji) {
    return (
      <div
        ref={ref}
        className={className}
        style={{
          fontSize: `${size}px`,
          lineHeight: 1,
          height: `${size}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style
        }}
        {...props}
      >
        {emoji}
      </div>
    );
  }

  const aspectRatio = src.width / src.height;
  return (
    <div
      ref={ref}
      className={className}
      style={{
        width: `${size * aspectRatio}px`,
        height: `${size}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
      {...props}
    >
      <img
        src={src.path}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }}
        alt=""
      />
    </div>
  );
});

Sprite.displayName = 'Sprite';